import type { AxiosResponse } from 'axios';
import interceptor, { baseUrl } from '../common/interceptor';
import { guardPreviewMutations } from '@/common/interceptor/interceptor';
import { getActiveToken } from '@/common/helper/tokenStorage';
import { APIPaths } from '../common/constants';
import type {
  AIChatFeedbackPayload,
  AskUserAnswers,
  AskUserRequest,
  ChatActionResultPayload,
  GetAIChatSessionsParams,
} from '@/components/AIChatDrawer/types';
import {
  normalizeActionResult,
  normalizeAskUser,
} from '@/components/AIChatDrawer/chatActions';
import { toHelpSource } from '@/components/AIChatDrawer/utils';
import type { HelpCenterAnswerSource } from '@/layout/AppHelpCenter/types';
import type {
  ChatChartDrilldown,
  ChatChartFilterCondition,
  ChatChartSpec,
} from '@/common/chat/parseChatMessageParts';

export interface AgentCoreChatMessagePayload {
  session_id: string;
  message: string;
  /** Only when this message answers an `ask_user` request -- `message` is
   * then the readable summary shown as the user's bubble. */
  answers?: AskUserAnswers;
}

export const StartAgentCoreChatSession = (
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.agentCoreChatStart, {}, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetAgentCoreChatSessions = (
  token?: string,
  params?: GetAIChatSessionsParams
): Promise<AxiosResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.search) {
    searchParams.set('search', params.search);
  }
  if (params?.page != null) {
    searchParams.set('page', String(params.page));
  }
  if (params?.page_size != null) {
    searchParams.set('page_size', String(params.page_size));
  }
  const query = searchParams.toString();
  const url = query
    ? `${APIPaths.agentCoreChatSessions}?${query}`
    : APIPaths.agentCoreChatSessions;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error while fetching AgentCore chat sessions:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const GetAgentCoreChatSession = (
  sessionId: string,
  token?: string,
  signal?: AbortSignal
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.agentCoreChatSession}${encodeURIComponent(sessionId)}/`,
        token,
        undefined,
        signal
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error while fetching AgentCore chat session:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const CreateAgentCoreChatFeedback = (
  payload: AIChatFeedbackPayload,
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.agentCoreChatFeedback, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error while creating AgentCore chat feedback:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const UpdateAgentCoreChatFeedback = (
  payload: AIChatFeedbackPayload,
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut('Put', APIPaths.agentCoreChatFeedback, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error while updating AgentCore chat feedback:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export interface AgentCoreChatDrilldownTeam {
  id: string;
  name: string;
  color: string | null;
}

export interface AgentCoreChatDrilldownShipment {
  id: string;
  reference_number: string;
  status: string;
  delivery_status: string;
  // Numeric status-pill code (see Shipment.SHIPMENT_TYPE_CHOICES on the
  // backend) -- a string here because it comes back through a CharField,
  // not the frontend's own StatusType alias yet.
  type: string;
  courier: string;
  origin_location: string;
  destination_location: string;
  team: string;
  teams: AgentCoreChatDrilldownTeam[];
  alert_status: string[];
  estimated_delivery_date: string | null;
  actual_delivery_date: string | null;
  created_at: string;
}

export interface AgentCoreChatDrilldownAlert {
  id: string;
  alert_type: string;
  title: string;
  detail: string;
  is_critical: boolean;
  is_resolved: boolean;
  is_dismissed: boolean;
  created_at: string;
  reference_number: string;
  courier: string;
}

export type AgentCoreChatDrilldownResult =
  | { entity: 'shipment'; results: AgentCoreChatDrilldownShipment[] }
  | { entity: 'alert'; results: AgentCoreChatDrilldownAlert[] };

/**
 * Resolves a click on a chat-rendered chart data point (see
 * ChatMessageChart's `onDrilldown`, fed by that point's own `drilldown`)
 * into the real shipment/alert records behind it -- the filters travel with
 * the point itself, not looked up server-side (see backend/agentcore_chat/
 * views.py's AgentCoreChatDrilldownAPIView for why that's still safe).
 */
export const GetAgentCoreChatDrilldown = (
  sessionId: string,
  entity: ChatChartDrilldown['entity'],
  filters: ChatChartFilterCondition[],
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.agentCoreChatDrilldown,
        { session_id: sessionId, entity, filters },
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error while fetching AgentCore chat drill-down:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export type AgentCoreChatStreamEvent =
  | { type: 'text'; text: string }
  | { type: 'query'; text: string }
  | { type: 'thought'; text: string }
  | { type: 'tool_start'; text: string }
  | { type: 'tool_end'; text: string }
  | { type: 'chart'; spec: ChatChartSpec }
  | { type: 'ask_user'; request: AskUserRequest }
  | { type: 'action_result'; result: ChatActionResultPayload }
  // Sent once per turn when the answer comes from help articles.
  | { type: 'help_source'; source: HelpCenterAnswerSource };

/**
 * Streams the agent's reply over SSE as `text`/`query`/`thought`/
 * `tool_start`/`tool_end`/`chart` frames — `text` arrives as one complete
 * frame per turn (the backend buffers the final answer rather than sending
 * token-by-token deltas); `query`/`thought`/`chart` may arrive earlier, once
 * per tool call (render_chart's own call produces a `chart` frame instead of
 * appearing in `text` -- the model never embeds chart JSON in its reply);
 * `tool_start`/`tool_end` bracket each tool call by name (the `text` field
 * carries the tool's name, not display text) and can overlap when multiple
 * tools run in parallel. `ask_user` asks the user for an action's inputs
 * (answered via the next message's `answers`); `action_result` carries an
 * action's outcome for a per-action renderer -- a malformed one of either
 * is dropped rather than surfaced. Uses `fetch` directly rather than the shared axios
 * interceptor, since axios cannot read a streaming response body
 * incrementally in the browser.
 */
export const StreamAgentCoreChatMessage = async (
  payload: AgentCoreChatMessagePayload,
  onEvent: (event: AgentCoreChatStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  // This bypasses the axios interceptor (see above), so it also bypasses the
  // interceptor's preview guard. The backend blocks it either way; this is
  // here so the user gets the same message as everywhere else rather than a
  // bare 403 toast.
  if (guardPreviewMutations()) {
    throw new Error('Read-only preview: write operations are blocked.');
  }
  const token = getActiveToken();
  const response = await fetch(`${baseUrl}${APIPaths.agentCoreChatChat}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      if (!part.startsWith('data: ')) continue;
      const parsed = JSON.parse(part.slice('data: '.length));
      if (parsed.error) throw new Error(parsed.error);
      if (typeof parsed.event === 'string') {
        onEvent({ type: 'text', text: parsed.event });
      } else if (typeof parsed.query === 'string') {
        onEvent({ type: 'query', text: parsed.query });
      } else if (typeof parsed.thought === 'string') {
        onEvent({ type: 'thought', text: parsed.thought });
      } else if (typeof parsed.tool_start === 'string') {
        onEvent({ type: 'tool_start', text: parsed.tool_start });
      } else if (typeof parsed.tool_end === 'string') {
        onEvent({ type: 'tool_end', text: parsed.tool_end });
      } else if (parsed.chart && typeof parsed.chart === 'object') {
        onEvent({ type: 'chart', spec: parsed.chart as ChatChartSpec });
      } else if (parsed.ask_user) {
        const request = normalizeAskUser(parsed.ask_user);
        if (request) onEvent({ type: 'ask_user', request });
      } else if (parsed.action_result) {
        const result = normalizeActionResult(parsed.action_result);
        if (result) onEvent({ type: 'action_result', result });
      } else if (parsed.help_source) {
        const source = toHelpSource(
          parsed.help_source.label,
          parsed.help_source.help_articles
        );
        if (source) onEvent({ type: 'help_source', source });
      }
    }
  }
};
